import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingBag, IndianRupee, Truck, MessageCircle, MapPin, Star, Search, ScanLine, Loader2, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import InlineAdBanner from "@/components/InlineAdBanner";
import AppCategoryChips from "./AppCategoryChips";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/** Generic product-first listing for food / grocery apps.
 *
 *  Backend: GET /api/products?app={khavayye|sabkuchfresh}&city=&search=&veg_only=
 *  Each item card shows the picture, name, price, veg-dot, vendor tag and an
 *  "Add to cart" button. Cart confirmation triggers the existing inquiry
 *  chat to the vendor (in-app notification). SMS/WhatsApp dispatch is
 *  MOCKED for now — wire Twilio later.
 */
function VegDot({ veg }) {
  if (veg == null) return null;
  return (
    <span
      title={veg ? "Veg" : "Non-Veg"}
      className={`inline-flex items-center justify-center h-4 w-4 rounded border ${veg ? "border-emerald-600" : "border-red-700"} shrink-0`}
    >
      <span className={`h-2 w-2 rounded-full ${veg ? "bg-emerald-600" : "bg-red-700"}`} />
    </span>
  );
}

export default function ProductFirstApp({ appId, theme, hero, cityKey = "city", chips = [] }) {
  const { t } = useLang();
  const cart = useCart();
  const [params, setParams] = useSearchParams();
  const city = params.get(cityKey) || "";
  const search = params.get("search") || "";
  const chip = params.get("chip") || "";
  const vegOnly = params.get("veg_only") === "1";
  const [searchDraft, setSearchDraft] = useState(search);
  const [cities, setCities] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Remember the shopper's last-picked city per app so a returning
  // visitor doesn't have to re-select. Runs once on mount if the URL
  // has no city yet.
  const cityStorageKey = `sb_last_city_${appId}`;
  useEffect(() => {
    if (!city) {
      try {
        const stored = window.localStorage.getItem(cityStorageKey);
        if (stored) {
          const np = new URLSearchParams(params);
          np.set(cityKey, stored);
          setParams(np, { replace: true });
        }
      } catch { /* localStorage may be unavailable — silently skip */ }
    }
  }, []);  // run once on mount only

  // Persist any subsequent city change so it survives a hard refresh
  // or navigating away and back.
  useEffect(() => {
    try {
      if (city) window.localStorage.setItem(cityStorageKey, city);
    } catch { /* noop */ }
  }, [city, cityStorageKey]);
  // Grocery-list OCR — only enabled for the grocery-family apps.
  const supportsListUpload = appId === "khavayye" || appId === "sabkuchfresh";
  const [listBusy, setListBusy] = useState(false);
  const [listResult, setListResult] = useState(null);
  const [listSelection, setListSelection] = useState({});

  const uploadListRef = React.useRef(null);
  const cityPickerRef = React.useRef(null);
  const [cityMissingHint, setCityMissingHint] = React.useState(false);

  // Gate: without a city we cannot match matched items to a nearby vendor,
  // so we force the shopper to pick a city before the file picker opens.
  const openListPicker = () => {
    if (!city) {
      setCityMissingHint(true);
      toast.error("Please select your city first so we can match items to local vendors");
      // Flash the city dropdown and scroll it into view
      try {
        cityPickerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        cityPickerRef.current?.focus();
      } catch { /* noop */ }
      return;
    }
    setCityMissingHint(false);
    uploadListRef.current?.click();
  };

  const handleListUpload = async (file) => {
    if (!file) return;
    if (!city) {
      // Safety net: even if the button is bypassed (e.g. drag-drop, keyboard),
      // block server calls until the city is chosen.
      toast.error("Please select your city first");
      setCityMissingHint(true);
      return;
    }
    setListBusy(true);
    setListResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const params2 = new URLSearchParams({ app: appId, city });
      const { data } = await api.post(`/products/parse-grocery-list?${params2}`, form);
      setListResult(data);
      const preselected = {};
      (data.items || []).forEach((r, i) => { if (r.matched) preselected[i] = true; });
      setListSelection(preselected);
      if (data.matched_count === 0) {
        toast.info("We couldn't match any items from the list — try a clearer photo.");
      } else {
        toast.success(`Matched ${data.matched_count} of ${data.detected_count} items — review and add to cart.`);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e?.message || "Could not read the list");
    } finally {
      setListBusy(false);
    }
  };

  const addMatchedToCart = async () => {
    const rows = (listResult?.items || []).filter((_, i) => listSelection[i] && (listResult.items[i].matched));
    let added = 0, failed = 0;
    for (const r of rows) {
      const m = r.matched;
      const qty = Math.max(1, Math.min(999, Math.round(r.quantity || 1)));
      try {
        // cart.add(vendor_id, product_name, quantity, note)
        await cart.add(m.vendor.vendor_id, m.name, qty, `From uploaded list · ${r.extracted}`);
        added += 1;
      } catch (e) {
        failed += 1;
      }
    }
    if (added > 0) toast.success(`Added ${added} item${added === 1 ? "" : "s"} to cart${failed ? ` · ${failed} failed` : ""}`);
    else toast.error("Could not add items to cart");
    setListResult(null);
    setListSelection({});
  };

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data || [])); }, []);

  useEffect(() => {
    setLoading(true);
    const q = { app: appId, page_size: 60 };
    if (city) q.city = city;
    if (search) q.search = search;
    if (chip) q.chip = chips.find((c) => c.id === chip)?.query || chip;
    if (vegOnly) q.veg_only = true;
    api.get("/products", { params: q })
      .then((r) => { setItems(r.data?.items || []); setTotal(r.data?.total || 0); })
      .finally(() => setLoading(false));
  }, [appId, city, search, chip, vegOnly, chips]);

  const updateParam = (key, val) => {
    const np = new URLSearchParams(params);
    if (val === false || val === "" || val == null) np.delete(key);
    else np.set(key, String(val));
    setParams(np);
  };

  const addToCart = async (item) => {
    try {
      await cart.add(item.vendor.vendor_id, item.name, 1, item.price_range || "");
      toast.success(`Added ${item.name} to enquiry cart`);
    } catch (e) {
      toast.error("Could not add to cart");
    }
  };

  return (
    <div className="min-h-screen">
      <section
        className="relative overflow-hidden"
        style={{ background: hero.background }}
        data-testid={`app-${appId}-hero`}
      >
        <div className="absolute inset-0" style={{ background: hero.overlay || "rgba(0,0,0,0.25)" }} />
        <div className="relative mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 py-16 sm:py-20 text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-medium">
            {hero.badge}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">{t(`apps.${appId}.name`)}</h1>
          <p className="mt-2 max-w-xl text-sm sm:text-base opacity-90">{t(`apps.${appId}.tagline`)}</p>
          <div className="mt-6 max-w-2xl bg-white rounded-2xl p-2 flex items-center gap-2 shadow-2xl shadow-black/20">
            <Input
              placeholder={hero.searchPlaceholder}
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && updateParam("search", searchDraft)}
              className="border-0 focus-visible:ring-0 text-foreground h-11"
              data-testid={`app-${appId}-search`}
            />
            <Select value={city || "all"} onValueChange={(v) => { updateParam(cityKey, v === "all" ? "" : v); if (v !== "all") setCityMissingHint(false); }}>
              <SelectTrigger
                ref={cityPickerRef}
                className={`h-11 w-36 border text-foreground bg-muted transition-all ${cityMissingHint && !city ? "border-red-500 ring-2 ring-red-500/40 animate-pulse" : "border-transparent"}`}
                data-testid={`app-${appId}-city`}
              >
                <SelectValue placeholder={t("vl.all_cities")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">{t("vl.all_cities")}</SelectItem>
                {cities.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              className="rounded-xl h-11 px-5 text-white"
              style={{ background: theme.primary }}
              onClick={() => updateParam("search", searchDraft)}
              data-testid={`app-${appId}-search-btn`}
            >
              {t(`apps.${appId}.cta`)}
            </Button>
          </div>
          {chips.length > 0 && (
            <AppCategoryChips
              chips={chips}
              active={chip}
              onChange={(id) => updateParam("chip", id || "")}
              testIdPrefix={`app-${appId}`}
            />
          )}
        </div>
      </section>

      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 pt-6" data-testid={`app-${appId}-adslot`}>
        <InlineAdBanner slot={`app-${appId}`} city={city} />
      </div>

      <section className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 py-8">
        {/* Toolbar */}
        <div className="mb-5 flex items-center gap-3 flex-wrap" data-testid={`app-${appId}-toolbar`}>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={vegOnly}
              onChange={(e) => updateParam("veg_only", e.target.checked ? "1" : "")}
              data-testid={`app-${appId}-veg-toggle`}
            />
            <VegDot veg={true} /> Pure Veg only
          </label>

          {supportsListUpload && (
            <>
              <input
                ref={uploadListRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                data-testid={`app-${appId}-list-file`}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleListUpload(f);
                  e.target.value = "";
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="rounded-full h-8"
                onClick={openListPicker}
                disabled={listBusy}
                data-testid={`app-${appId}-list-upload-btn`}
                style={{ borderColor: theme.primary, color: theme.primary }}
                title={!city ? "Select a city first" : "Upload a handwritten or printed shopping list"}
              >
                {listBusy ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Reading list…</>
                ) : (
                  <><ScanLine className="h-3.5 w-3.5 mr-1" /> Upload grocery list</>
                )}
              </Button>
            </>
          )}

          <span className="text-xs text-muted-foreground ml-auto">{total.toLocaleString()} items{cart.count > 0 && (
            <>
              {" · "}
              <Link to="/cart" className="underline" data-testid={`app-${appId}-go-cart`}>
                {cart.count} in cart →
              </Link>
            </>
          )}</span>
        </div>

        {loading ? (
          <p className="text-center text-muted-foreground py-12">{t("vl.loading")}</p>
        ) : items.length === 0 ? (
          <Card className="p-10 border-border/60 text-center" data-testid={`app-${appId}-empty`}>
            <p className="text-muted-foreground">No items match your filters yet.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid={`app-${appId}-grid`}>
            {items.map((it, idx) => (
              <React.Fragment key={it.product_key}>
                {idx > 0 && idx % 10 === 0 && (
                  <div className="col-span-2 sm:col-span-3 lg:col-span-4 xl:col-span-5" data-testid={`app-${appId}-grid-ad-${idx}`}>
                    <InlineAdBanner slot={`app-${appId}`} city={city} />
                  </div>
                )}
                <Card className="overflow-hidden border-border/60 flex flex-col hover:shadow-lg transition-shadow" data-testid={`app-${appId}-item-${it.product_key}`}>
                  <div className="aspect-square w-full bg-muted relative">
                    {it.image ? (
                      <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${theme.primary}22, ${theme.accent || theme.primary}55)` }} />
                    )}
                    {it.is_veg != null && (
                      <div className="absolute top-1.5 left-1.5">
                        <VegDot veg={it.is_veg} />
                      </div>
                    )}
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="font-semibold text-sm leading-tight line-clamp-2">{it.name}</div>
                    <Link
                      to={`/vendors/${it.vendor.vendor_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                      data-testid={`app-${appId}-vendor-tag-${it.product_key}`}
                    >
                      <ShoppingBag className="h-3 w-3" />
                      <span className="truncate">by {it.vendor.business_name}</span>
                    </Link>
                    <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3" /> {it.vendor.city}
                      {it.vendor.rating > 0 && (
                        <span className="inline-flex items-center gap-0.5 ml-1"><Star className="h-3 w-3 fill-amber-500 text-amber-500" />{Number(it.vendor.rating).toFixed(1)}</span>
                      )}
                    </div>
                    {it.price_range && (
                      <div className="mt-1 font-heading text-base inline-flex items-center" style={{ color: theme.primary }}>
                        <IndianRupee className="h-3.5 w-3.5" /> {it.price_range.replace("₹", "")}
                      </div>
                    )}
                    {it.description && (
                      <p className="text-[11px] text-muted-foreground line-clamp-2 mt-1">{it.description}</p>
                    )}
                    <div className="mt-auto pt-2 flex gap-1.5">
                      <Button
                        size="sm"
                        className="rounded-full flex-1 text-white text-xs h-8"
                        style={{ background: theme.primary }}
                        onClick={() => addToCart(it)}
                        data-testid={`app-${appId}-add-${it.product_key}`}
                      >
                        + Cart
                      </Button>
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="rounded-full h-8 px-2"
                        data-testid={`app-${appId}-chat-${it.product_key}`}
                      >
                        <Link to={`/vendors/${it.vendor.vendor_id}?chat=1`} title="Chat with vendor">
                          <MessageCircle className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </Card>
              </React.Fragment>
            ))}
          </div>
        )}
      </section>

      {/* Floating cart CTA */}
      {cart.count > 0 && (
        <div className="fixed bottom-4 inset-x-0 z-30 px-4 pointer-events-none" data-testid={`app-${appId}-cart-cta`}>
          <Link
            to="/cart"
            className="pointer-events-auto mx-auto max-w-md flex items-center gap-3 rounded-full text-white px-5 py-3 shadow-2xl shadow-black/20"
            style={{ background: theme.primary }}
          >
            <ShoppingBag className="h-5 w-5" />
            <span className="flex-1 font-semibold">{cart.count} in cart · review &amp; confirm</span>
            <span className="text-sm">→</span>
          </Link>
        </div>
      )}

      {/* Grocery-list review modal — shown after the AI OCR has run */}
      <Dialog open={!!listResult} onOpenChange={(o) => !o && setListResult(null)}>
        <DialogContent className="max-w-2xl" data-testid={`app-${appId}-list-review`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5" />
              Review the items we read from your list
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {listResult?.matched_count || 0} of {listResult?.detected_count || 0} items matched our catalog.
            Uncheck anything you don&apos;t want, then add the rest to your cart. Unmatched items are shown for reference.
          </p>
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-border/60 border border-border/60 rounded-xl" data-testid={`app-${appId}-list-review-rows`}>
            {(listResult?.items || []).map((r, i) => (
              <div
                key={i}
                className="p-2 flex items-center gap-3 bg-background"
                data-testid={`app-${appId}-list-row-${i}`}
              >
                <input
                  type="checkbox"
                  checked={!!listSelection[i]}
                  onChange={(e) => setListSelection((s) => ({ ...s, [i]: e.target.checked }))}
                  disabled={!r.matched}
                  className="h-4 w-4 shrink-0"
                  data-testid={`app-${appId}-list-check-${i}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <span className="font-medium">{r.extracted}</span>
                    <span className="text-muted-foreground text-xs ml-2">× {r.quantity} {r.unit}</span>
                  </div>
                  {r.matched ? (
                    <div className="text-[11px] text-emerald-700 truncate">
                      → {r.matched.name}
                      <span className="text-muted-foreground"> · {r.matched.vendor.business_name}</span>
                      {r.matched.price_range && <span className="text-muted-foreground"> · {r.matched.price_range}</span>}
                    </div>
                  ) : (
                    <div className="text-[11px] text-amber-700">No catalog match — will be skipped</div>
                  )}
                </div>
                {r.matched?.image && (
                  <img src={r.matched.image} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="flex-col sm:flex-row sm:items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => setListResult(null)}
              data-testid={`app-${appId}-list-cancel`}
            >
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={addMatchedToCart}
              disabled={!Object.values(listSelection).some(Boolean)}
              style={{ background: theme.primary }}
              className="text-white"
              data-testid={`app-${appId}-list-add-btn`}
            >
              <ShoppingBag className="h-4 w-4 mr-1" />
              Add {Object.values(listSelection).filter(Boolean).length} to cart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
